<!-- eslint-disable max-lines -->
<template>
	<ManageInterationServiceLayout
		:current-step.sync="currentStep"
		:steps.sync="steps"
		:is-edit="isEdit"
		:clone-mode="cloneMode"
		:user-full-permissions="userFullPermissions"
		:back-button-action="goBackToOverview"
		:integration-service="integrationService"
		:integration-service-has-changed="integrationServiceHasChanged"
		:brand-id="isEdit ? integrationServiceToEdit.brandId : userBrands[0]"
		:has-valid-json="hasValidJson"
		:is-direct-to-s3="isDirectToS3"
		:request-body-mapping-type="requestBodyMappingType"
		@finish="finish"
	>
		<template #step-0>
			<DataSelection
				v-if="currentStep === 0"
				:is-edit="isEdit"
				:is-new-integration="isNewIntegration"
				:integration-service-enabled="integrationServiceEnabled"
				:cassie-object-id="integrationService.cassieObjectId"
				:data-type-items="dataTypeItems"
				:selected-fields="integrationService.objectSelectList"
				:integration-filters="integrationService.filters"
				:object-id-column="integrationService.objectIdColumn"
				:reset-incremental-value="integrationService.resetIncrementalValue"
				:increment-field-items="incrementFieldItems"
				:available-fields="availableFields"
				:object-client-urn-column="integrationService.objectClientUrnColumn"
				:returned-value="integrationService.returnedValue"
				:lookup-data="lookupData"
				:user-full-permissions="userFullPermissions"
				@update:cassieObjectId="integrationService.cassieObjectId = $event"
				@update:objectClientUrnColumn="integrationService.objectClientUrnColumn = $event"
				@update:returnedValue="integrationService.returnedValue = $event"
				@update:objectIdColumn="integrationService.objectIdColumn = $event"
				@update:resetIncrementalValue="handleResetIncrementalValueUpdate($event)"
				@update:objectSelectList="integrationService.objectSelectList = $event"
				@update:integrationFilters="integrationService.filters = $event"
			/>
		</template>
		<template #step-1>
			<IntegrationBuilder
				v-if="currentStep === 1"
				:is-edit="isEdit"
				:cassie-object-id="integrationService.cassieObjectId"
				:object-id-column="integrationService.objectIdColumn"
				:reset-incremental-value="integrationService.resetIncrementalValue"
				:cassie-objects="lookupData.cassieObjects"
				:data-type-items="dataTypeItems"
				:http-method="integrationService.httpMethod"
				:request-url="integrationService.requestUrl"
				:request-body="integrationService.requestBody"
				:request-throttling-wait-time-ms="integrationService.requestThrottlingWaitTimeMS"
				:custom-headers="integrationService.customHeaders"
				:http-method-items="httpMethodItems"
				:object-select-list="integrationService.objectSelectList"
				:integration-filters="integrationService.filters"
				:user-full-permissions="userFullPermissions"
				:clone-mode="cloneMode"
				:system-integration-items="systemIntegrationItems"
				:integration-id="integrationService.integrationId"
				@update:cassieObjectId="integrationService.cassieObjectId = $event"
				@update:objectIdColumn="integrationService.objectIdColumn = $event"
				@update:resetIncrementalValue="integrationService.resetIncrementalValue = $event"
				@update:httpMethod="integrationService.httpMethod = $event"
				@update:requestUrl="integrationService.requestUrl = $event"
				@update:requestBody="integrationService.requestBody = $event"
				@update:requestThrottlingWaitTimeMs="integrationService.requestThrottlingWaitTimeMS = $event"
				@update:customHeaders="integrationService.customHeaders = $event"
				@update:integrationId="integrationService.integrationId = $event"
				@update:objectSelectList="integrationService.objectSelectList = $event"
				@update:filters="integrationService.filters = $event"
			/>
		</template>
		<template #step-2>
			<RequestPayloadMapping
				v-if="currentStep === 2"
				:request-body="integrationService.requestBody"
				:user-full-permissions="userFullPermissions"
				:is-edit="isEdit"
				:clone-mode="cloneMode"
				:selected-fields="selectedFields"
				:field-usage-map="fieldUsageMap"
				:available-fields="availableFields"
				@update:requestBody="integrationService.requestBody = $event"
				@update:mappingType="requestBodyMappingType = $event"
				@field-drop="handleFieldDrop"
			/>
		</template>
		<template #step-3>
			<IntegrationService
				v-if="currentStep === 3"
				:user-full-permissions="userFullPermissions"
				:integration-service.sync="integrationService"
				:lookup-data="lookupData"
				:system-integration-items="systemIntegrationItems"
				:step="3"
				:steps="steps"
				:look-up-data="lookUpData"
				:clone-mode="cloneMode"
				:has-template="hasTemplate"
				@reload-look-up-data="loadIntegrationServices"
			/>
		</template>
		<template #modal>
			<!-- Save Modal -->
			<SaveIntegrationModal
				v-if="showSaveModal"
				:current-name="integrationService.name"
				:current-notes="integrationService.notes"
				:is-edit="isEdit"
				:saving="isSaving"
				@save="handleModalSave"
				@cancel="handleModalCancel"
			/>
		</template>
	</ManageInterationServiceLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import IntegrationService from './integration-service.vue'
import IntegrationBuilder from './integration-builder/IntegrationBuilder.vue'
import RequestPayloadMapping from './integration-builder/RequestPayloadMapping.vue'
import DataSelection from './data-selection/DataSelection.vue'
import SaveIntegrationModal from './SaveIntegrationModal.vue'
import { CONNECTOR_SERVICE } from '../../../../router/route-names.js'
import ManageInterationServiceLayout from './manage-integration-service-layout.vue'
import { CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { brandOptions } from '../../../../../../shared/state/brands.js'
import { updatePrivacyPolicies } from '../../../../../../shared/state/privacy-policies.js'
import { postIntegrationService, putIntegrationService, getIntegrationServices } from '../../../../../../shared/utils/api/connector-service.js'
import { resolveEndpointPrefill } from '../../../../../../shared/lib/system-integration-templates.js'

export default {
	components: { ManageInterationServiceLayout, IntegrationService, IntegrationBuilder, RequestPayloadMapping, DataSelection, SaveIntegrationModal },
	props: {
		integrationServiceToEdit: Object,
		lookupData: Object,
		systemIntegrationItems: Array,
		cloneMode: Boolean,
		systemIntegrationId: Number,
		serviceTemplate: Object,
		systemAuthenticationTypeId: Number
	},
	setup () {
		return {
			brandOptions,
			updatePrivacyPolicies,
			CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS
		}
	},
	data () {
		const baseIntegrationService = {
			name: null,
			brandId: 0, // Always set to 0 for new integrations
			integrationId: this.systemIntegrationId || null,
			notes: null,
			isEnabled: false,
			httpMethod: null,
			authenticationUrl: null,
			requestUrl: null,
			requestBody: null,
			requestThrottlingWaitTimeMS: 0,
			customHeaders: [],
			successResponses: [],
			cassieObjectId: null,
			objectIdColumn: null,
			objectClientUrnColumn: null,
			returnedValue: null,
			returnedValueMappings: [],
			trigger: 'Insert',
			resetIncrementalValue: true,
			updateStandDak: false,
			objectSelectList: [],
			objectFilterClause: null,
			clientTargetObjectName: null,
			targetSystemAction: 'Insert',
			filters: []
		}

		const newIntegrationService = this.applyServiceTemplate(baseIntegrationService)

		return {
			currentStep: 0,
			steps: [
				{ slot: 0, title: 'Data Selection & Filtering', stepNumber: 0, stepComplete: false },
				{ slot: 1, title: 'Connection & HTTP', stepNumber: 1, stepComplete: false },
				{ slot: 2, title: 'Request Payload Mapping', stepNumber: 2, stepComplete: false },
				{ slot: 3, title: 'Connection Response', stepNumber: 3, stepComplete: false }
			],
			integrationService: this.ensureBackwardCompatibility(JSON.parse(JSON.stringify(this.integrationServiceToEdit || newIntegrationService))),
			lookUpData: {},
			fetchedLookupData: null,
			showSaveModal: false,
			isSaving: false,
			requestBodyMappingType: 'classic' // Track mapping type: 'classic' or 'javascript'
		}
	},
	computed: {
		...mapGetters('auth', ['userBrands', 'productAreaPermission']),
		isEdit () {
			return !!this.integrationServiceToEdit && !this.cloneMode
		},
		isDirectToS3 () {
			return this.systemAuthenticationTypeId === 5
		},
		isNewIntegration () {
			return !this.isEdit
		},
		userFullPermissions () {
			if (this.isEdit) {
				const hasProductPerm = this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS)
				const isServiceEditable = this.integrationService && this.integrationService.isEditable
				const hasBrand = Array.isArray(this.userBrands) && this.userBrands.length > 0 && this.userBrands.includes(this.integrationService && this.integrationService.brandId)
				return hasProductPerm && isServiceEditable && hasBrand
			}
			return this.productAreaPermission(CONNECTOR_SERVICE_MODULE_FULL_PERMISSIONS)
		},
		integrationServiceEnabled () {
			return this.integrationService.isEnabled
		},
		integrationServiceHasChanged () {
			return JSON.stringify(this.integrationServiceToEdit) !== JSON.stringify(this.integrationService)
		},
		hasTemplate () {
			return !!this.serviceTemplate
		},
		dataTypeItems () {
			const currentLookupData = this.lookupData || this.fetchedLookupData

			if (!currentLookupData || !currentLookupData.cassieObjects || !Array.isArray(currentLookupData.cassieObjects)) {
				return []
			}

			return currentLookupData.cassieObjects.map(({ cassieObjectId, cassieObjectName, isUpdateStanddak, cassieObjectFields }) => ({
				text: cassieObjectName,
				value: cassieObjectId,
				isUpdateStanddak: isUpdateStanddak,
				fieldCount: cassieObjectFields ? cassieObjectFields.length : 0
			}))
		},
		httpMethodItems () {
			if (!this.lookupData || !this.lookupData.httpMethods || !Array.isArray(this.lookupData.httpMethods)) {
				return []
			}

			return this.lookupData.httpMethods
		},
		availableFields () {
			const currentLookupData = this.lookupData || this.fetchedLookupData

			if (!this.integrationService.cassieObjectId || !currentLookupData || !currentLookupData.cassieObjects) {
				return []
			}

			const selectedObject = currentLookupData.cassieObjects.find(obj => obj.cassieObjectId === this.integrationService.cassieObjectId)
			if (!selectedObject || !selectedObject.cassieObjectFields) {
				return []
			}

			return selectedObject.cassieObjectFields.map(field => ({
				fieldName: field.cassieObjectFieldName,
				displayName: field.cassieObjectFieldDisplayName || field.cassieObjectFieldName,
				dataType: field.dataType,
				typeFriendlyName: field.typeFriendlyName || field.systemTypeName || 'Text',
				isFilterable: field.isFilterable !== false, // Default to true unless explicitly false
				isIncremental: field.isIncremental || false
			}))
		},
		incrementFieldItems () {
			const currentLookupData = this.lookupData || this.fetchedLookupData

			if (!this.integrationService.cassieObjectId || !currentLookupData || !currentLookupData.cassieObjects) {
				return []
			}

			const selectedObject = currentLookupData.cassieObjects.find(obj => obj.cassieObjectId === this.integrationService.cassieObjectId)
			if (!selectedObject || !selectedObject.cassieObjectFields) {
				return []
			}

			return selectedObject.cassieObjectFields
				.filter(field => field.isIncremental)
				.map(field => ({
					text: field.cassieObjectFieldDisplayName || field.cassieObjectFieldName,
					value: field.cassieObjectFieldName
				}))
		},
		selectedFields () {
			return this.integrationService.objectSelectList || []
		},
		fieldUsageMap () {
			return {}
		},
		hasValidJson () {
			const body = this.integrationService && this.integrationService.requestBody
			if (body === undefined || body === null) return false
			if (typeof body === 'object') return true
			if (typeof body === 'string') {
				const trimmed = body.trim()
				if (!trimmed) return false
				try {
					const parsed = JSON.parse(trimmed)
					return parsed !== null && typeof parsed === 'object'
				} catch (error) {
					return false
				}
			}
			return false
		}
	},
	async created () {
		if (this.cloneMode) {
			this.integrationService.name += ' (clone)'
			this.integrationService.isEditable = true
			this.integrationService.cloneFromId = this.integrationService.id
			delete this.integrationService.id
		}
		if (this.isEdit) this.setAllStepsToComplete()

		// Fetch lookup data if not provided as props
		if (!this.lookupData) {
			await this.fetchLookupData()
		}
		if (this.isEdit) {
			this.integrationServiceEnabled ? this.integrationService.resetIncrementalValue = false : this.integrationService.resetIncrementalValue = true
		}
	},
	methods: {
		/**
		 * Fetches lookup data from API if not provided as props
		 */
		async fetchLookupData () {
			try {
				const response = await getIntegrationServices()
				if (response && response.lookupData) {
					this.fetchedLookupData = response.lookupData
				}
			} catch (error) {
				console.error('Failed to fetch lookup data:', error)
				showSnackbar({
					text: 'Failed to load data type options. Please refresh the page.',
					color: 'error'
				})
			}
		},
		/**
		 * Ensures backward compatibility with existing integration data
		 */
		ensureBackwardCompatibility (integrationService) {
			// Convert objectSelectList from string to array if needed
			if (typeof integrationService.objectSelectList === 'string') {
				integrationService.objectSelectList = integrationService.objectSelectList
					.split(',')
					.filter(field => field.trim())
					.map(field => field.trim())
			}

			// Ensure objectSelectList is an array
			if (!Array.isArray(integrationService.objectSelectList)) {
				integrationService.objectSelectList = []
			}

			// Ensure filters is an array
			if (!Array.isArray(integrationService.filters)) {
				integrationService.filters = []
			}

			// Ensure customHeaders is an array
			if (!Array.isArray(integrationService.customHeaders)) {
				integrationService.customHeaders = []
			}

			// Ensure returnedValueMappings is an array
			if (!Array.isArray(integrationService.returnedValueMappings)) {
				integrationService.returnedValueMappings = []
			}

			// Migrate legacy single returnedValue to returnedValueMappings array
			if (integrationService.returnedValue && integrationService.objectClientUrnColumn &&
				integrationService.returnedValueMappings.length === 0) {
				integrationService.returnedValueMappings = [{
					cassieField: integrationService.objectClientUrnColumn,
					responseField: integrationService.returnedValue
				}]
			}

			// Set defaults for missing properties
			if (!integrationService.requestBody) {
				integrationService.requestBody = ''
			}

			if (!integrationService.requestUrl) {
				integrationService.requestUrl = ''
			}

			if (!integrationService.httpMethod) {
				integrationService.httpMethod = 'POST'
			}

			return integrationService
		},
		applyServiceTemplate (baseService) {
			if (!this.serviceTemplate) {
				return baseService
			}
			const templateDefaults = { ...baseService }

			// Apply basic template information
			// Use endpoint name if available (from template card title), otherwise fall back to template name
			if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.name) {
				templateDefaults.name = this.serviceTemplate.selectedEndpoint.name
			} else if (this.serviceTemplate.serviceEndpoint && this.serviceTemplate.serviceEndpoint.name) {
				templateDefaults.name = this.serviceTemplate.serviceEndpoint.name
			} else if (this.serviceTemplate.name) {
				templateDefaults.name = `${this.serviceTemplate.name} Service`
			}

			if (this.serviceTemplate.description) {
				templateDefaults.notes = this.serviceTemplate.description
			}

			// Apply endpoint information if available
			if (this.serviceTemplate.endpoints && this.serviceTemplate.endpoints.length > 0) {
				// Pre-fill from the endpoint the admin actually picked (selectedEndpoint, falling back
				// to serviceEndpoint, then endpoints[0]) so URL/method always agree with the object
				// binding, body and headers taken from the same endpoint below. defaults.endpointAddress
				// only fills in for a relative or missing endpoint URL; it never overwrites an absolute one.
				const { endpoint: prefillEndpoint, requestUrl, httpMethod } = resolveEndpointPrefill(this.serviceTemplate)

				if (requestUrl) {
					templateDefaults.requestUrl = requestUrl
				}

				if (httpMethod) {
					templateDefaults.httpMethod = httpMethod
				}

				if (prefillEndpoint.description) {
					templateDefaults.notes = templateDefaults.notes
						? `${templateDefaults.notes}\n\nEndpoint: ${prefillEndpoint.description}`
						: prefillEndpoint.description
				}

				// apply selected endpoint information
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.cassieObjectID) {
					templateDefaults.cassieObjectId = this.serviceTemplate.selectedEndpoint.cassieObjectID
				}
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.incrementField) {
					templateDefaults.objectIdColumn = this.serviceTemplate.selectedEndpoint.incrementField
				}
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.fields.length > 0) {
					templateDefaults.objectSelectList = this.serviceTemplate.selectedEndpoint.fields.map(field => field.name)
				}
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.headers.length > 0) {
					templateDefaults.customHeaders = this.serviceTemplate.selectedEndpoint.headers.map(header => ({
						headerName: header.Name,
						headerValue: header.value
					}))
				}
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.selectedEndpoint.requestBody) {
					try {
						// Parse and re-stringify to ensure proper JSON formatting
						const parsedBody = JSON.parse(this.serviceTemplate.selectedEndpoint.requestBody)
						templateDefaults.requestBody = JSON.stringify(parsedBody, null, 2)
					} catch (error) {
						// If parsing fails, use the original value as-is
						console.warn('Failed to format requestBody JSON:', error)
						templateDefaults.requestBody = this.serviceTemplate.selectedEndpoint.requestBody
					}
				}
				if (this.serviceTemplate.selectedEndpoint && this.serviceTemplate.successResponses && this.serviceTemplate.selectedEndpoint.successResponses.length > 0) {
					templateDefaults.successResponses = this.serviceTemplate.selectedEndpoint.successResponses.map(response => ({
						expectedSuccessResponseCode: response.code,
						expectedSuccessResponseMessage: response.message
					}))
				} else {
					templateDefaults.successResponses = [
						{ expectedSuccessResponseCode: 200, expectedSuccessResponseMessage: 'Success' },
						{ expectedSuccessResponseCode: 201, expectedSuccessResponseMessage: 'Created' },
						{ expectedSuccessResponseCode: 204, expectedSuccessResponseMessage: 'No Content' }
					]
				}
			}

			// Apply default configuration from the template
			// (defaults.endpointAddress is consumed by resolveEndpointPrefill above; overwriting the
			// endpoint URL here is exactly the defect that broke multi-endpoint templates)
			if (this.serviceTemplate.defaults) {
				if (this.serviceTemplate.defaults.authenticationUrl) {
					templateDefaults.authenticationUrl = this.serviceTemplate.defaults.authenticationUrl
				}

				// Per-service request throttle (milliseconds), read directly from the template. This is
				// distinct from the system's execution cadence (config.API_Integrations.FrequencyInSeconds):
				// they were previously conflated on defaults.frequencyInSeconds, which meant a rate-limit
				// throttle value also aggressively lowered the poll interval.
				if (this.serviceTemplate.defaults.requestThrottlingWaitTimeMS) {
					templateDefaults.requestThrottlingWaitTimeMS = this.serviceTemplate.defaults.requestThrottlingWaitTimeMS
				}
			}

			// Set reasonable defaults for template-based services
			templateDefaults.isEnabled = false // User should manually enable
			templateDefaults.trigger = 'Insert' // Most common use case
			templateDefaults.targetSystemAction = 'Insert' // Most common action

			// Ensure resetIncrementalValue is always properly initialized
			if (templateDefaults.resetIncrementalValue === undefined || templateDefaults.resetIncrementalValue === null) {
				templateDefaults.resetIncrementalValue = false
			}

			return templateDefaults
		},
		goBackToOverview () {
			this.$router.push({
				name: CONNECTOR_SERVICE,
				params: {
					accessedVia: 'integration-services'
				}
			})
		},

		setAllStepsToComplete () {
			this.steps.forEach(step => {
				step.stepComplete = true
			})
		},
		async finish () {
			this.showSaveModal = true
		},
		async handleModalSave ({ name, notes }) {
			this.isSaving = true

			try {
				const integrationService = JSON.parse(JSON.stringify(this.integrationService))
				integrationService.name = name
				integrationService.notes = notes

				// For Direct to S3, enforce requestUrl to "/" as it won't be used
				if (this.isDirectToS3) {
					integrationService.requestUrl = '/'
				}

				const matchingDataType = this.dataTypeItems.find(
					({ value }) => value === this.integrationService.cassieObjectId
				)
				const isUpdateStanddak = matchingDataType?.isUpdateStanddak || false

				if (!isUpdateStanddak) {
					integrationService.objectClientUrnColumn = null
					integrationService.returnedValue = null
					integrationService.updateStandDak = false
				}

				if (Array.isArray(integrationService.objectSelectList)) {
					if (integrationService.objectSelectList.length > 0 && typeof integrationService.objectSelectList[0] === 'object' && integrationService.objectSelectList[0].cassieObjectFieldName) {
						integrationService.objectSelectList = integrationService.objectSelectList.map(item => item.cassieObjectFieldName).join(',')
					} else {
						integrationService.objectSelectList = integrationService.objectSelectList.join(',')
					}
				}

				integrationService.filters = this.normalizeBlankFilters(integrationService.filters)
				delete integrationService.failures

				if (!this.isEdit || this.cloneMode) {
					await postIntegrationService(integrationService)
				} else {
					await putIntegrationService(integrationService)
				}

				showSnackbar(`This integration has been ${this.isEdit ? 'updated' : 'added'}`)
				this.showSaveModal = false
				this.goBackToOverview()
			} catch (error) {
				console.error('Error saving integration:', error)
				// The error will be handled by the API utility, just reset saving state
			} finally {
				this.isSaving = false
			}
		},
		handleModalCancel () {
			this.showSaveModal = false
		},
		normalizeBlankFilters (filters) {
			if (!Array.isArray(filters)) return filters
			return filters.map(filter => {
				const operator = filter?.filter?.operator
				if (operator !== 'IS' && operator !== 'IS NOT') return filter
				const values = filter?.filter?.values ?? []
				if (values.some(({ value }) => value != null)) return filter
				return {
					...filter,
					filter: {
						...filter.filter,
						values: [{ value: 'NULL' }]
					}
				}
			})
		},
		loadIntegrationServices () {},
		handleFieldDrop (dropData) {},
		handleResetIncrementalValueUpdate (value) {
			this.$set(this.integrationService, 'resetIncrementalValue', value)
			this.$forceUpdate()
		}
	}
}
</script>
